// ระบบจัดการการขอเข้าร่วมทีมระหว่างน้องและโม
// รองรับการขอเข้าร่วมทีมทั้ง 2 ฝ่าย: น้องขอเข้าทีม และโมเชิญน้องเข้าทีม

import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// สถานะของคำขอเข้าร่วมทีม
export const REQUEST_STATUS = {
  PENDING: 'pending',     // รอยืนยัน
  ACCEPTED: 'accepted',   // ยอมรับ
  REJECTED: 'rejected',   // ปฏิเสธ
  CANCELLED: 'cancelled'  // ยกเลิก
};

// ประเภทของคำขอ
export const REQUEST_TYPE = {
  GIRL_TO_MOMO: 'girl_to_momo',  // น้องขอเข้าทีมโม
  MOMO_TO_GIRL: 'momo_to_girl',  // โมเชิญน้องเข้าทีม
  LEAVE_TEAM: 'leave_team'        // ขอยกเลิกร่วมทีม
};

/**
 * สร้างคำขอเข้าร่วมทีม (น้องขอเข้าทีมโม)
 * @param {string} girlId - ID ของน้อง
 * @param {string} momoId - ID ของโม
 * @param {string} message - ข้อความเพิ่มเติม
 * @returns {Promise<string|null>} - ID ของคำขอ หรือ null หากเกิดข้อผิดพลาด
 */
export const createGirlToMomoRequest = async (girlId, momoId, message = '') => {
  try {
    // ตรวจสอบว่ามีคำขอที่รอยืนยันอยู่แล้วหรือไม่
    const existingRequest = await getPendingRequest(girlId, momoId);
    if (existingRequest) {
      throw new Error('มีคำขอเข้าร่วมทีมที่รอยืนยันอยู่แล้ว');
    }
    
    const requestData = {
      type: REQUEST_TYPE.GIRL_TO_MOMO,
      girlId: girlId,
      momoId: momoId,
      status: REQUEST_STATUS.PENDING,
      message: message,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'teamRequests'), requestData);
    console.log(`✅ สร้างคำขอเข้าร่วมทีม (น้อง→โม) สำเร็จ: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถสร้างคำขอเข้าร่วมทีม (น้อง→โม):', error);
    throw error;
  }
};

/**
 * สร้างคำขอเข้าร่วมทีม (โมเชิญน้องเข้าทีม)
 * @param {string} momoId - ID ของโม
 * @param {string} girlId - ID ของน้อง
 * @param {string} message - ข้อความเพิ่มเติม
 * @returns {Promise<string|null>} - ID ของคำขอ หรือ null หากเกิดข้อผิดพลาด
 */
export const createMomoToGirlRequest = async (momoId, girlId, message = '') => {
  try {
    // ตรวจสอบว่ามีคำขอที่รอยืนยันอยู่แล้วหรือไม่
    const existingRequest = await getPendingRequest(girlId, momoId);
    if (existingRequest) {
      throw new Error('มีคำขอเข้าร่วมทีมที่รอยืนยันอยู่แล้ว');
    }
    
    const requestData = {
      type: REQUEST_TYPE.MOMO_TO_GIRL,
      girlId: girlId,
      momoId: momoId,
      status: REQUEST_STATUS.PENDING,
      message: message,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'teamRequests'), requestData);
    console.log(`✅ สร้างคำขอเข้าร่วมทีม (โม→น้อง) สำเร็จ: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถสร้างคำขอเข้าร่วมทีม (โม→น้อง):', error);
    throw error;
  }
};

/**
 * ดึงคำขอที่รอยืนยันระหว่างน้องและโม
 * @param {string} girlId - ID ของน้อง
 * @param {string} momoId - ID ของโม
 * @returns {Promise<object|null>}
 */
export const getPendingRequest = async (girlId, momoId) => {
  try {
    const requestsRef = collection(db, 'teamRequests');
    const q = query(
      requestsRef,
      where('girlId', '==', girlId),
      where('momoId', '==', momoId),
      where('status', '==', REQUEST_STATUS.PENDING)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.docs.length > 0) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    
    return null;
  } catch (error) {
    console.error('❌ ไม่สามารถดึงคำขอที่รอยืนยัน:', error);
    return null;
  }
};

/**
 * ดึงคำขอเข้าร่วมทีมทั้งหมดของน้อง
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<Array>}
 */
export const getGirlRequests = async (girlId) => {
  try {
    const requestsRef = collection(db, 'teamRequests');
    const q = query(
      requestsRef,
      where('girlId', '==', girlId)
    );
    
    const snapshot = await getDocs(q);
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt descending
    return requests.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('❌ ไม่สามารถดึงคำขอของน้อง:', error);
    return [];
  }
};

/**
 * ดึงคำขอเข้าร่วมทีมทั้งหมดของโม
 * @param {string} momoId - ID ของโม
 * @returns {Promise<Array>}
 */
export const getMomoRequests = async (momoId) => {
  try {
    const requestsRef = collection(db, 'teamRequests');
    const q = query(
      requestsRef,
      where('momoId', '==', momoId)
    );
    
    const snapshot = await getDocs(q);
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt descending
    return requests.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('❌ ไม่สามารถดึงคำขอของโม:', error);
    return [];
  }
};

/**
 * ดึงรายการคำขอยกเลิกร่วมทีมที่เกี่ยวข้องกับผู้ใช้
 * @param {string} userId - ID ของผู้ใช้
 * @param {string} userRole - role ของผู้ใช้ ('girl' หรือ 'momo')
 * @returns {Promise<Array>}
 */
export const getLeaveTeamRequests = async (userId, userRole) => {
  try {
    let q;
    if (userRole === 'girl') {
      // ดึงคำขอที่น้องเป็นผู้ขอ หรือเป็นเป้าหมาย
      q = query(
        collection(db, 'teamRequests'),
        where('type', '==', REQUEST_TYPE.LEAVE_TEAM),
        where('requesterRole', '==', 'girl'),
        where('requesterId', '==', userId)
      );
    } else if (userRole === 'momo') {
      // ดึงคำขอที่โมเป็นผู้ขอ หรือเป็นเป้าหมาย
      q = query(
        collection(db, 'teamRequests'),
        where('type', '==', REQUEST_TYPE.LEAVE_TEAM),
        where('requesterRole', '==', 'momo'),
        where('requesterId', '==', userId)
      );
    }
    
    if (!q) return [];
    
    const snapshot = await getDocs(q);
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt descending
    return requests.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('❌ ไม่สามารถดึงคำขอยกเลิกร่วมทีม:', error);
    return [];
  }
};

/**
 * ยอมรับคำขอเข้าร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const acceptTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    const requestSnap = await getDoc(requestRef);
    
    if (!requestSnap.exists()) {
      throw new Error('ไม่พบคำขอ');
    }
    
    const requestData = requestSnap.data();
    
    // อัปเดตสถานะคำขอ
    await updateDoc(requestRef, {
      status: REQUEST_STATUS.ACCEPTED,
      updatedAt: new Date()
    });
    
    // เพิ่มน้องให้โมดูแล
    const { assignGirlToMomo } = await import('./roleManager');
    await assignGirlToMomo(requestData.momoId, requestData.girlId);
    
    console.log(`✅ ยอมรับคำขอเข้าร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถยอมรับคำขอเข้าร่วมทีม:', error);
    throw error;
  }
};

/**
 * ปฏิเสธคำขอเข้าร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const rejectTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    await updateDoc(requestRef, {
      status: REQUEST_STATUS.REJECTED,
      updatedAt: new Date()
    });
    
    console.log(`✅ ปฏิเสธคำขอเข้าร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถปฏิเสธคำขอเข้าร่วมทีม:', error);
    throw error;
  }
};

/**
 * สร้างคำขอยกเลิกร่วมทีม
 * @param {string} requesterId - ID ของผู้ขอ (น้องหรือโม)
 * @param {string} requesterRole - role ของผู้ขอ ('girl' หรือ 'momo')
 * @param {string} targetId - ID ของเป้าหมาย (โมหรือน้อง)
 * @param {string} message - ข้อความเพิ่มเติม
 * @returns {Promise<string|null>} - ID ของคำขอ หรือ null หากเกิดข้อผิดพลาด
 */
export const createLeaveTeamRequest = async (requesterId, requesterRole, targetId, message = '') => {
  try {
    // ตรวจสอบว่ามีคำขอที่รอยืนยันอยู่แล้วหรือไม่
    const existingRequest = await getPendingLeaveRequest(requesterId, targetId);
    if (existingRequest) {
      throw new Error('มีคำขอยกเลิกร่วมทีมที่รอยืนยันอยู่แล้ว');
    }
    
    const requestData = {
      type: REQUEST_TYPE.LEAVE_TEAM,
      requesterId: requesterId,
      requesterRole: requesterRole,
      targetId: targetId,
      status: REQUEST_STATUS.PENDING,
      message: message,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'teamRequests'), requestData);
    console.log(`✅ สร้างคำขอยกเลิกร่วมทีมสำเร็จ: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถสร้างคำขอยกเลิกร่วมทีม:', error);
    throw error;
  }
};

/**
 * ดึงคำขอยกเลิกร่วมทีมที่รอยืนยัน
 * @param {string} requesterId - ID ของผู้ขอ
 * @param {string} targetId - ID ของเป้าหมาย
 * @returns {Promise<object|null>}
 */
export const getPendingLeaveRequest = async (requesterId, targetId) => {
  try {
    const q = query(
      collection(db, 'teamRequests'),
      where('type', '==', REQUEST_TYPE.LEAVE_TEAM),
      where('requesterId', '==', requesterId),
      where('targetId', '==', targetId),
      where('status', '==', REQUEST_STATUS.PENDING)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('❌ ไม่สามารถดึงคำขอยกเลิกร่วมทีม:', error);
    return null;
  }
};

/**
 * ยอมรับคำขอยกเลิกร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const acceptLeaveTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    const requestSnap = await getDoc(requestRef);
    
    if (!requestSnap.exists()) {
      throw new Error('ไม่พบคำขอ');
    }
    
    const requestData = requestSnap.data();
    
    // อัปเดตสถานะคำขอ
    await updateDoc(requestRef, {
      status: REQUEST_STATUS.ACCEPTED,
      updatedAt: new Date()
    });
    
    // ลบน้องออกจากการดูแลของโม
    const { removeGirlFromMomo } = await import('./roleManager');
    await removeGirlFromMomo(requestData.targetId, requestData.requesterId);
    
    console.log(`✅ ยอมรับคำขอยกเลิกร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถยอมรับคำขอยกเลิกร่วมทีม:', error);
    throw error;
  }
};

/**
 * ปฏิเสธคำขอยกเลิกร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const rejectLeaveTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    await updateDoc(requestRef, {
      status: REQUEST_STATUS.REJECTED,
      updatedAt: new Date()
    });
    
    console.log(`✅ ปฏิเสธคำขอยกเลิกร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถปฏิเสธคำขอยกเลิกร่วมทีม:', error);
    throw error;
  }
};

/**
 * ยกเลิกคำขอเข้าร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const cancelTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    await updateDoc(requestRef, {
      status: REQUEST_STATUS.CANCELLED,
      updatedAt: new Date()
    });
    
    console.log(`✅ ยกเลิกคำขอเข้าร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถยกเลิกคำขอเข้าร่วมทีม:', error);
    throw error;
  }
};

/**
 * ลบคำขอเข้าร่วมทีม
 * @param {string} requestId - ID ของคำขอ
 * @returns {Promise<boolean>}
 */
export const deleteTeamRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'teamRequests', requestId);
    await deleteDoc(requestRef);
    
    console.log(`✅ ลบคำขอเข้าร่วมทีมสำเร็จ: ${requestId}`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถลบคำขอเข้าร่วมทีม:', error);
    throw error;
  }
};

/**
 * ดึงรายการโมที่สามารถเชิญน้องเข้าทีมได้
 * @returns {Promise<Array>}
 */
export const getAvailableMomo = async () => {
  try {
    const { getUsersByRole } = await import('./roleManager');
    const momos = await getUsersByRole('momo');
    
    return momos.map(momo => ({
      id: momo.id,
      displayName: momo.displayName || momo.email,
      email: momo.email,
      managedGirls: momo.managedGirls || []
    }));
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายการโมที่ว่าง:', error);
    return [];
  }
};

/**
 * ดึงรายการน้องที่สามารถเชิญเข้าทีมได้
 * @returns {Promise<Array>}
 */
export const getAvailableGirls = async () => {
  try {
    const { getAllGirls } = await import('./girlProfileManager');
    const girls = await getAllGirls();
    
    // กรองเฉพาะน้องที่ยังไม่ได้เข้าร่วมทีม
    return girls.filter(girl => !girl.momoId);
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายการน้องที่ว่าง:', error);
    return [];
  }
};

/**
 * ดึงข้อมูลคำขอพร้อมข้อมูลผู้ใช้
 * @param {Array} requests - รายการคำขอ
 * @returns {Promise<Array>}
 */
export const enrichRequestsWithUserData = async (requests) => {
  try {
    const enrichedRequests = [];
    
    for (const request of requests) {
      // ดึงข้อมูลน้อง
      const girlRef = doc(db, 'girls', request.girlId);
      const girlSnap = await getDoc(girlRef);
      const girlData = girlSnap.exists() ? girlSnap.data() : null;
      
      // ดึงข้อมูลโม
      const momoRef = doc(db, 'users', request.momoId);
      const momoSnap = await getDoc(momoRef);
      const momoData = momoSnap.exists() ? momoSnap.data() : null;
      
      enrichedRequests.push({
        ...request,
        girlData: girlData ? { id: request.girlId, ...girlData } : null,
        momoData: momoData ? { id: request.momoId, ...momoData } : null
      });
    }
    
    return enrichedRequests;
  } catch (error) {
    console.error('❌ ไม่สามารถดึงข้อมูลผู้ใช้สำหรับคำขอ:', error);
    return requests;
  }
};
